import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getPartnersAppOnboardingPartnersGet, onboardingCompleteAppOnboardingCompletePost } from '@/client/sdk.gen'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import type { Partner } from '@/hooks/use-onboarding'
import { StepLayout } from './-components/step-layout'
import { PartnerCard } from './-components/partner-card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Plus, Search, Zap } from 'lucide-react'
import { buildLogoUrl, normalizeDomain } from '@/lib/logo'

export const Route = createFileRoute('/onboarding/partners')({
  component: PartnersStep,
})

const CUSTOM_PARTNER_ID_PREFIX = 'custom:'

// Tailwind grid breakpoints we mirror in JS so the row virtualizer knows how
// many cards fit per row. Keep in sync with the gridTemplateColumns logic
// below.
function columnsForWidth(width: number): number {
  if (width >= 1024)
    return 6
  if (width >= 768)
    return 5
  if (width >= 640)
    return 4
  return 3
}

// Card height: ~96px (icon + two-line label + padding) plus 12px gap. A
// slightly generous estimate gives the virtualizer headroom; it measures
// real heights as rows render.
const ESTIMATED_ROW_HEIGHT = 132

function PartnersStep() {
  useOnboardingStepViewed('partners')
  const navigate = useNavigate()
  const {
    email,
    password,
    firstName,
    lastName,
    domain,
    company,
    qualificationArguments,
    qualificationLogic,
    companyTerritory,
    userTerritories,
    teamInvites,
    inviteToken,
    updateState,
  } = useOnboarding()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()
  // Guards against both rapid dblclicks (React state isn't in the DOM fast
  // enough to disable the button between clicks) and resubmits after browser
  // back from /onboarding/verify-email-sent. Once we've submitted in this
  // component's lifetime, we won't submit again.
  const hasSubmittedRef = useRef(false)

  const [directory, setDirectory] = useState<Partner[]>([])
  const [customPartners, setCustomPartners] = useState<Partner[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  useEffect(() => {
    let cancelled = false
    getPartnersAppOnboardingPartnersGet()
      .then(({ data, error }) => {
        if (cancelled)
          return
        if (error || !data) {
          console.error('Failed to fetch partners directory:', error)
          setIsLoading(false)
          return
        }
        const mapped: Partner[] = data.map(entry => ({
          id: entry.id,
          name: entry.name,
          logo: entry.logo,
          status: entry.status as Partner['status'],
        }))
        setDirectory(mapped)
        setIsLoading(false)
      })
      .catch((error: unknown) => {
        if (cancelled)
          return
        console.error('Failed to fetch partners directory:', error)
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const sortedDirectory = useMemo(
    () => [...directory].toSorted((a, b) => a.name.localeCompare(b.name)),
    [directory],
  )

  // Custom partners are selected entries, not directory entries. Keep them
  // out of the browse grid so a newly-added partner does not render twice.
  const selectablePartners = useMemo(
    () => [...customPartners, ...sortedDirectory],
    [customPartners, sortedDirectory],
  )

  const trimmedSearch = search.trim().toLowerCase()
  const visiblePartners = useMemo(() => {
    if (!trimmedSearch)
      return sortedDirectory
    return sortedDirectory.filter(p => p.name.toLowerCase().includes(trimmedSearch))
  }, [sortedDirectory, trimmedSearch])

  const selectedPartners = useMemo(
    () => selectablePartners.filter(p => selectedIds.has(p.id)),
    [selectablePartners, selectedIds],
  )

  const handleToggle = useCallback((partner: Partner) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(partner.id)) {
        next.delete(partner.id)
      }
      else {
        next.add(partner.id)
      }
      return next
    })
  }, [])

  const handleAddCustomPartner = useCallback((name: string, customDomain: string) => {
    const trimmedName = name.trim()
    if (!trimmedName)
      return
    const cleanedDomain = normalizeDomain(customDomain)
    const id = `${CUSTOM_PARTNER_ID_PREFIX}${crypto.randomUUID()}`
    const newPartner: Partner = {
      id,
      name: trimmedName,
      logo: cleanedDomain ? buildLogoUrl(cleanedDomain) : '',
      status: 'not_on_platform',
      domain: cleanedDomain || undefined,
    }
    setCustomPartners(prev => [newPartner, ...prev])
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
    // Clearing the search snaps the directory back to its full default view
    // so the user sees their new selection in the "Your partners" strip and
    // can keep browsing.
    setSearch('')
  }, [])

  const handleNext = () => {
    if (hasSubmittedRef.current)
      return
    hasSubmittedRef.current = true

    const selected = selectedPartners
    updateState({
      selectedPartners: selected,
      currentStep: 7,
    })

    setIsSubmitting(true)
    setSubmitError(undefined)
    onboardingCompleteAppOnboardingCompletePost({
      body: {
        email,
        password,
        firstName,
        lastName,
        organizationName: company.name,
        companyDomain: domain,
        companyDescription: company.businessDescription || undefined,
        qualificationArguments: qualificationArguments || undefined,
        qualificationLogic: qualificationLogic || undefined,
        countryName: company.countryName || undefined,
        regionName: company.regionName || undefined,
        companyTerritory,
        userTerritories: userTerritories.map(t => ({
          name: t.name,
          regions: t.regions,
        })),
        teamInvites,
        selectedPartners: selected.map(p => ({
          partnerId: p.id,
          partnerName: p.name,
          domain: p.domain,
        })),
        partnerInviteToken: inviteToken || undefined,
      },
    })
      .then(({ error: respError }) => {
        if (respError) {
          const detail = (respError as { detail?: string }).detail
          throw new Error(detail || 'Something went wrong')
        }
        sessionStorage.removeItem('onboarding-state')
        // replace:true so that Back from /verify-email-sent doesn't return
        // the user to the partners step where they could submit again.
        navigate({ to: '/onboarding/verify-email-sent', search: { email }, replace: true })
      })
      .catch((error: unknown) => {
        // If submit failed, let the user retry — clear the guard.
        hasSubmittedRef.current = false
        setSubmitError(error instanceof Error ? error.message : 'Something went wrong')
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleBack = () => {
    navigate({ to: '/onboarding/team' })
  }

  if (isLoading) {
    return (
      <StepLayout
        title="Select your partners"
        subtitle="Loading partner directory..."
        onNext={() => {}}
        onBack={handleBack}
        nextLabel="Continue"
        nextDisabled
        maxWidth="xl"
      >
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          Loading partners...
        </div>
      </StepLayout>
    )
  }

  return (
    <StepLayout
      title="Select your partners"
      subtitle="Choose the companies you work with."
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="Complete setup"
      nextDisabled={isSubmitting}
      maxWidth="xl"
      stickyFooter
    >
      {submitError && (
        <Alert className="mb-6 border-destructive/50 bg-destructive/10">
          <AlertDescription className="text-destructive">{submitError}</AlertDescription>
        </Alert>
      )}

      <Alert className="mb-6 border-primary/20 bg-primary/5">
        <Zap className="h-4 w-4 text-primary" />
        <AlertTitle>Unlock cross-company insights</AlertTitle>
        <AlertDescription>
          Adding partners lets us map shared territories, compare market coverage, and surface warm introductions across your network.
        </AlertDescription>
      </Alert>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search partners by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {selectedPartners.length > 0 && (
        <div className="mb-6">
          <div className="mb-3 text-xs font-medium text-muted-foreground">
            {`Your Partners (${selectedPartners.length})`}
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {selectedPartners.map(partner => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                isSelected
                onClick={() => handleToggle(partner)}
              />
            ))}
          </div>
        </div>
      )}

      {trimmedSearch && visiblePartners.length === 0
        ? (
            <CustomPartnerEmptyState
              // Keying on the search text resets the popover form's local
              // state when the user keeps typing — they expect "what I
              // searched" to prefill the name field without a setState-in-
              // effect.
              key={trimmedSearch}
              search={search.trim()}
              onAdd={handleAddCustomPartner}
            />
          )
        : (
            <PartnerDirectoryGrid
              partners={visiblePartners}
              selectedIds={selectedIds}
              onToggle={handleToggle}
            />
          )}
    </StepLayout>
  )
}

interface PartnerDirectoryGridProps {
  partners: Partner[]
  selectedIds: Set<string>
  onToggle: (partner: Partner) => void
}

/**
 * Virtualized grid over the (potentially ~1.7K-entry) partner directory.
 *
 * Why virtualize: rendering all directory entries at once is fine for the DOM
 * tree itself, but each card is an `<img>` and Google's favicon endpoint
 * starts dropping requests under heavy parallel load. Virtualization keeps
 * only the visible rows mounted, so only ~30 favicons fetch at a time as the
 * user scrolls.
 *
 * The scroll element is the onboarding layout's `<main>` (which has
 * overflow-y-auto); we discover it on mount by walking up the DOM tree.
 */
function PartnerDirectoryGrid({ partners, selectedIds, onToggle }: PartnerDirectoryGridProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollElRef = useRef<HTMLElement>(null)
  const [cols, setCols] = useState(6)

  // Walk up to the onboarding layout's <main> (overflow-y-auto) so the
  // virtualizer scrolls the page, not an inner box. A callback ref runs
  // before useVirtualizer's first read of getScrollElement so the lookup
  // resolves on first render without a forced refresh.
  const wrapperCallbackRef = useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node
    if (!node)
      return
    let el: HTMLElement | null = node
    while (el && el.tagName.toLowerCase() !== 'main') {
      el = el.parentElement
    }
    scrollElRef.current = el
  }, [])

  useEffect(() => {
    const el = wrapperRef.current
    if (!el)
      return
    const update = () => setCols(columnsForWidth(el.clientWidth))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      ro.disconnect()
    }
  }, [])

  const rowCount = Math.max(1, Math.ceil(partners.length / cols))

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 4,
  })

  if (partners.length === 0) {
    return (
      <div ref={wrapperCallbackRef} className="py-10 text-center text-sm text-muted-foreground">
        No partners to show.
      </div>
    )
  }

  const virtualRows = virtualizer.getVirtualItems()

  return (
    <div ref={wrapperCallbackRef}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const rowStart = virtualRow.index * cols
          const rowItems = partners.slice(rowStart, rowStart + cols)
          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className="absolute left-0 right-0 grid gap-3 pb-3"
              style={{
                top: virtualRow.start,
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              }}
            >
              {rowItems.map(partner => (
                <PartnerCard
                  key={partner.id}
                  partner={partner}
                  isSelected={selectedIds.has(partner.id)}
                  onClick={() => onToggle(partner)}
                />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface CustomPartnerEmptyStateProps {
  search: string
  onAdd: (name: string, domain: string) => void
}

/**
 * Shown when a search yields no directory matches. A single card-shaped tile
 * keeps the visual rhythm of the directory grid; clicking it opens a popover
 * with the manual-entry form (name required, domain optional).
 */
function CustomPartnerEmptyState({ search, onAdd }: CustomPartnerEmptyStateProps) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(search)
  const [customDomain, setCustomDomain] = useState('')

  const trimmedName = name.trim()
  const canSubmit = trimmedName.length > 0

  const handleSubmit = () => {
    if (!canSubmit)
      return
    onAdd(trimmedName, customDomain)
    setOpen(false)
  }

  return (
    <div>
      <div className="mb-3 text-sm text-muted-foreground">
        {`No partners match "${search}".`}
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-4 text-center transition-all hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                <Plus className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="text-xs font-medium leading-tight">Add custom partner</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80">
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">Add custom partner</h4>
                <p className="text-xs text-muted-foreground">
                  We'll include them in your network.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="custom-partner-name">Partner name</Label>
                <Input
                  id="custom-partner-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      handleSubmit()
                  }}
                  placeholder="Acme Industrial"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="custom-partner-domain">
                  {'Website '}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="custom-partner-domain"
                  value={customDomain}
                  onChange={e => setCustomDomain(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')
                      handleSubmit()
                  }}
                  placeholder="acme.com"
                />
              </div>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add partner
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
